import { defineStore } from 'pinia'
import { GalleryItem, MediaResource, GalleryCollection } from '@/store/gallery/types'
import * as seratoMediaStock from '@/serato-media-stock'
import * as pexels from 'pexels'
import { seratoMediaItemToGalleryItem, seratoCollectionToGalleryCollection, collectionNameSorter } from '@/utils/gallery-helper'
import useStoreErrorHandler from '@/composables/UseStoreErrorHandler'

const { handleSearchError } = useStoreErrorHandler()
const pexelsClient = pexels.createClient(
  '563492ad6f91700001000001db42b1bb4fd4422192bbe90373aeec8c'
)

/**
 * Type assertion function to determine if a given API response is an error. This is required as Pexels SDK does not
 * throw when there is an error (i.e. every SDK method is 'valid').
 * @param response API response from Pexels SDK
 * @returns Whether or not the response is an error response object
 */
const isError = (response: unknown): response is pexels.ErrorResponse => {
  return typeof response === 'object' && response !== null && 'error' in response
}

/**
 * This method throws an error if a Pexels API call responds with an error. Unfortunately the SDK does not throw if
 * there was an error, and the return values from the SDK are not always the same type. This function unwraps the proper
 * type if it is not an error.
 *
 * @param response API response from Pexels SDK
 * @returns API response type, with `ErrorResponse` removed from list of types
 */
const throwIfError = <T>(response: pexels.ErrorResponse | T): T => {
  if (isError(response)) {
    throw new Error(response.error)
  }
  return response
}

/**
 * Type for Pexel Photo dimensions
 */
type PhotoDimension = {
  height: number
  width: number | null
  dpr?: number
}

/**
 * Dimensions defined in the Pexel documentation
 * For 'large' and 'large2x' these are the max dimensions while maintaining the aspect ratio 
 * So if portrait it'll be max 650px high, if landscape its max 940px wide
 */
const photoDimensions: Record<string, PhotoDimension> = {
  large: { height: 650, width: 940, dpr: 1 },
  large2x: { height: 650, width: 940, dpr: 2 },
  medium: { height: 350, width: null },
  small: { height: 130, width: null },
  portrait: { height: 1200, width: 800 },
  landscape: { height: 627, width: 1200 },
  tiny: { height: 200, width: 280 }
}

/**
 * Sorting function which sorts by dimensions in pixels (width x height)
 * @param a First element to compare
 * @param b Second element to compare
 * @returns -1 if a < b, 0 if a == b, 1 if a > b
 */
const mediaSizeComparator = (a: MediaResource, b: MediaResource): number => {
  const aSize = (a.width ?? 0) * (a.height ?? 0)
  const bSize = (b.width ?? 0) * (b.height ?? 0)
  return (aSize === bSize) ? 0 : (aSize < bSize) ? -1 : 1
}

const collectionsOrder = [
  'Vibes',
  'Colours',
  'Retro',
  'Trippy',
  'Textures',
  'Vibrant',
  'Dark',
  'Chill',
  'Environments',
  'Soft'
]

const pexelsCollectionToGalleryCollection = (collection: pexels.Collection): GalleryCollection => {
  return {
    id: collection.id,
    title: collection.title,
    source: 'pexels'
  }
}

const pexelsPhotoToGalleryItem = (photo: pexels.Photo): GalleryItem => {
  const widthToHeightRatio = photo.width / photo.height
  // Get sources
  const sources: MediaResource[] = []

  for (const [key, src] of Object.entries(photo.src)) {
    // Ignore the cropped image sources
    if (key === 'landscape' || key === 'portrait') {
      continue
    }

    const dimensions = photoDimensions[key]
    if (dimensions) {
      let { width, height } = dimensions

      // For medium and small images calculate width
      if (width === null) {
        width = height * widthToHeightRatio
      }

      // Have correct dimensions for large images
      if (key === 'large' || key === 'large2x') {
        // portrait 
        if (widthToHeightRatio < 1) {
          width = height * widthToHeightRatio
        }
        // landscape
        if (widthToHeightRatio > 1 && dimensions.width) {
          height = dimensions.width / widthToHeightRatio
        }
      }

      sources.push({
        width: width,
        height: height,
        src: src
      })
    } else {
      // For the original image source
      sources.push({
        width: photo.width,
        height: photo.height,
        src: photo.src.original
      })
    }
  }

  sources.sort(mediaSizeComparator)

  // Get image source, the 'medium' size is most appropriate quality for thumbnail
  const previewDimensions = photoDimensions['medium']
  previewDimensions.width = widthToHeightRatio * previewDimensions.height

  return {
    type: 'image',
    widthToHeightRatio,
    preview: {
      imageSource: {
        src: photo.src.medium,
        width: previewDimensions.width,
        height: previewDimensions.height
      },
      videoSource: null
    },
    source: sources
  }
}

const pexelsVideoToGalleryItem = (video: pexels.Video): GalleryItem => {
  const widthToHeightRatio = video.width / video.height
  const sources: MediaResource[] = []

  for (const file of video.video_files) {
    if (file.width && file.height) {
      sources.push({
        width: file.width,
        height: file.height,
        src: file.link
      })
    }
  }

  sources.sort(mediaSizeComparator)
  const smallestVideo = sources[0]

  return {
    type: 'video',
    widthToHeightRatio,
    preview: {
      imageSource: {
        src: video.image,
        width: null,
        height: null
      },
      videoSource: smallestVideo
    },
    source: sources
  }
}

/**
 * Type assertion function to check if the media provided is a Pexels Photo object, as media from Collection
 * has an extra 'type' property
 * @param media
 */
const isPexelPhoto = (media: (pexels.Photo | pexels.Video) & { type: 'Photo' | 'Video' }): media is pexels.Photo & { type: 'Photo' | 'Video' } => {
  return media.type === 'Photo'
}

// Search formats, and chip tag order
export enum SearchType {
  VIDEO = 'Videos',
  PHOTO = 'Photos'
}

export interface BackgroundGalleryState {
  items: GalleryItem[]
  collections: GalleryCollection[]
  page: number
  totalResultItems: number
  itemsPerPage: number
  searchType: SearchType
  searchPhrase: string | null
  selectedCollection: GalleryCollection | null
  isCollectionsAvailable: boolean
}

export const useBackgroundGalleryStore = defineStore('backgroundGalleryStore', {
  state: (): BackgroundGalleryState => ({
    items: [],
    collections: [],
    page: 1,
    totalResultItems: 0,
    itemsPerPage: 15,
    searchType: SearchType.VIDEO,
    searchPhrase: null,
    selectedCollection: null,
    isCollectionsAvailable: true
  }),
  actions: {
    async clearItems (): Promise<void> {
      // this needs to be inside an async/await function because it triggers nextTick for watch to pick up the changes.
      this.items = []
    },
    async fetchByKeyword (keyword: string, page = 1): Promise<void> {
      if (page === 1) {
        await this.clearItems()
      }
      try {
        switch (this.searchType) {
        case SearchType.PHOTO: {
          const response = throwIfError(
            await pexelsClient.photos.search({
              query: keyword,
              per_page: this.itemsPerPage,
              page
            })
          )
          this.items.push(...response.photos.map((photo) => pexelsPhotoToGalleryItem(photo)))
          this.totalResultItems = response.total_results
          break
        }
        case SearchType.VIDEO: {
          const response = throwIfError(
            await pexelsClient.videos.search({
              query: keyword,
              per_page: this.itemsPerPage,
              page
            })
          )
          this.items.push(...response.videos.map((video) => pexelsVideoToGalleryItem(video)))
          this.totalResultItems = response.total_results
          break
        }
        }
      } catch {
        handleSearchError('Pexels')
      }
      this.page = page
      this.searchPhrase = keyword
    },

    async fetchByCollection (collection: GalleryCollection, page = 1): Promise<void> {
      this.selectedCollection = collection
      if (page === 1) {
        await this.clearItems()
      }
      switch (collection.source) {
      case 'pexels': {
        const response = await this.handlePexelsCall(async () => await pexelsClient.collections.media({ id: collection.id, page }))
        if (response) {
          const items = response.media.map((media) => {
            if (isPexelPhoto(media)) {
              return pexelsPhotoToGalleryItem(media)
            } else {
              return pexelsVideoToGalleryItem(media)
            }
          })
          this.items.push(...items)
          this.totalResultItems = response.total_results
        }
        break
      }
      case 'serato': {
        const response = seratoMediaStock.fetchBackgroundsByCollectionId(collection.id, page, this.itemsPerPage)
        const items = response.pageItems.map((mediaItem) => {
          return seratoMediaItemToGalleryItem(mediaItem)
        })
        this.items.push(...items)
        this.totalResultItems = response.totalResults
        break
      }
      }
      this.page = page
      this.searchPhrase = null
    },
    async fetchCollections (): Promise<void> {
      this.collections = []
      // Fetching pexels collections
      const response = await this.handlePexelsCall(async () => await pexelsClient.collections.all())
      response?.collections.forEach(collection => {
        this.collections.push(pexelsCollectionToGalleryCollection(collection))
      })
      // Fetching Serato media stock collections
      seratoMediaStock.fetchBackgroundCollections().forEach(collection => {
        this.collections.push(seratoCollectionToGalleryCollection(collection))
      })
      this.collections.sort(collectionNameSorter(collectionsOrder))
    },
    async nextPage (): Promise<void> {
      const pages = Math.ceil(this.totalResultItems / this.itemsPerPage)

      if (this.page < pages) {
        if (this.searchPhrase) {
          this.fetchByKeyword(this.searchPhrase, ++this.page)
        } else if (this.selectedCollection) {
          this.fetchByCollection(this.selectedCollection, ++this.page)
        }
      }
    },
    /**
     * wrapper function used to capture pexels responses and exceptions
     * @param pexelsCall async function doing the http call to pexels.
     * @returns pexels response or undefined in case of exception
     */
    async handlePexelsCall<T> (pexelsCall: () => Promise<pexels.ErrorResponse | T>): Promise<T | undefined> {
      try {
        return throwIfError(await pexelsCall())
      } catch {
        this.isCollectionsAvailable = false
        return undefined
      }
    }
  }
})
